import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Fragment, useEffect } from "react";
import { validarRut, formatearRut } from "../../../utilities/RutUtil";
import { useGlobalContext } from "../../../Config";


const FieldRow = ({ id, label, children }) => (
  <div className="flex flex-column md:flex-row md:align-items-center gap-1 md:gap-2">
    <label
      htmlFor={id}
      className="text-700 font-medium text-xs md:text-sm w-full md:w-9rem"
    >
      {label}
    </label>
    <div className="flex-1">{children}</div>
  </div>
);

const Filtros = ({
  data,
  onSearch,
  onExport,
  onClear,
  loading,
  exporting,
  filters,
  setFilters,
  variant,
  unificadoDinar = false,
  selectedVariant,
  onVariantChange,
}) => {
  const { isDinar, isSack } = useGlobalContext();

  const getVariantConfig = () => {
    const sackBase = [
      "dates",
      "folios",
      "clienteCodigo",
      "clienteRut",
      "sackSelects",
      "sackCanalOrigenEstado",
      "visualizacion",
    ];
    const dinarBase = [
      "dates",
      "folios",
      "clienteCodigo",
      "clienteRut",
      "dinarRazonSocial",
      "dinarProducto",
    ];

    switch (variant) {
      case "TIEMPO":
        return { sections: ["dates", "vendedor"], folioLabels: null };
      case "NOTAVENTA":
        return {
          sections: isDinar
            ? [
                ...(unificadoDinar ? ["tipoReporte"] : []),
                "folios",
                "notaVentaFolios",
                "dates",
                "clienteCodigo",
                "vendedor",
                "clienteRut",
                "dinarRazonSocial",
                "dinarProducto",
                "notaVentaRadios",
              ]
            : isSack
            ? sackBase
            : ["dates"],
          folioLabels: null,
        };

      case "COTIZACION":
        return {
          sections: isDinar
            ? [
                ...(unificadoDinar ? ["tipoReporte"] : []),
                "folios",
                "dates",
                "clienteCodigo",
                "vendedor",
                "clienteRut",
                "dinarRazonSocial",
                "dinarProducto",
              ]
            : isSack
            ? sackBase
            : ["dates"],
        };

      case "BLOQUEADA":
        return {
          sections: isDinar
            ? [
                ...(unificadoDinar ? ["tipoReporte"] : []),
                "folios",
                "dates",
                "clienteCodigo",
                "vendedor",
                "clienteRut",
                "dinarRazonSocial",
                "dinarProducto",
              ]
            : isSack
            ? sackBase
            : ["dates"],
          folioLabels: null,
        };
      case "NOCONFIRMADOS":
        return { sections: ["dates"], folioLabels: null };
      default:
        return null;
    }
  };

  const config = getVariantConfig();

  useEffect(() => {
    if (filters?.fechaDesde && filters?.fechaHasta) return;
    const desdeDefault = new Date();
    desdeDefault.setMonth(desdeDefault.getMonth() - 1);
    const hastaDefault = new Date();
    setFilters((prev) => ({
      ...prev,
      fechaDesde: prev?.fechaDesde ?? desdeDefault,
      fechaHasta: prev?.fechaHasta ?? hastaDefault,
    }));
  }, [filters?.fechaDesde, filters?.fechaHasta, setFilters]);

  if (!config) return null;

  const handleDateChange = (e, field) => {
    setFilters((prev) => ({ ...prev, [field]: e.value }));
  };

  const handleDropdownChange = (e, field) => {
    setFilters((prev) => ({ ...prev, [field]: e.value }));
  };

  const handleClearLocal = () => {
    setFilters({
      fechaDesde: null,
      fechaHasta: null,
      folioDesde: "",
      folioHasta: "",
      clienteCodigo: "",
      clienteRut: "",
      razonSocial: "",
      oficina: null,
      listaPrecios: null,
      vendedor: null,
      canal: null,
      origen: null,
      estado: variant === "NOTAVENTA" ? "todos" : null,
      tipodetipoentrega: "todos",
      codigoproducto: "",
      descripcionproducto: "",
      notaVentaDesde: "",
      notaVentaHasta: "",
      visualizacion: "ultimas",
      historia: false,
    });
    if (onClear) onClear();
  };

  const SectionDates = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="fechaDesde" label="Fecha Desde">
          <Calendar
            id="fechaDesde"
            value={filters.fechaDesde}
            onChange={(e) => handleDateChange(e, "fechaDesde")}
            showIcon
            dateFormat="dd/mm/yy"
            className="w-full"
            inputClassName="p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
          />
        </FieldRow>
      </div>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="fechaHasta" label="Fecha Hasta">
          <Calendar
            id="fechaHasta"
            value={filters.fechaHasta}
            onChange={(e) => handleDateChange(e, "fechaHasta")}
            showIcon
            dateFormat="dd/mm/yy"
            className="w-full"
            inputClassName="p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
          />
        </FieldRow>
      </div>
    </>
  );

  const SectionFolios = () => {
    const labelDesde = config.folioLabels?.desde ?? "Folio Desde";
    const labelHasta = config.folioLabels?.hasta ?? "Folio Hasta";
    const placeholderDesde = config.folioLabels?.desde
      ? "Ingrese N° de Nota Venta Desde"
      : "Ingrese N° de Folio Desde";
    const placeholderHasta = config.folioLabels?.hasta
      ? "Ingrese N° de Nota Venta Hasta"
      : "Ingrese N° de Folio Hasta";

    return (
      <>
        <div className="col-12 md:col-6 lg:col-6">
          <FieldRow id="folioDesde" label={labelDesde}>
            <InputText
              id="folioDesde"
              value={filters.folioDesde}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                setFilters((prev) => ({ ...prev, folioDesde: value }));
              }}
              className="w-full p-inputtext-sm text-xs md:text-sm"
              disabled={loading}
              placeholder={placeholderDesde}
            />
          </FieldRow>
        </div>

        <div className="col-12 md:col-6 lg:col-6">
          <FieldRow id="folioHasta" label={labelHasta}>
            <InputText
              id="folioHasta"
              value={filters.folioHasta}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                setFilters((prev) => ({ ...prev, folioHasta: value }));
              }}
              className="w-full p-inputtext-sm text-xs md:text-sm"
              disabled={loading}
              placeholder={placeholderHasta}
            />
          </FieldRow>
        </div>
      </>
    );
  };

  const SectionClienteCodigo = () => (
    <div className="col-12 md:col-6 lg:col-6">
      <FieldRow id="clienteCodigo" label="Cliente">
        <InputText
          id="clienteCodigo"
          value={filters.clienteCodigo}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, "");
            setFilters((prev) => ({ ...prev, clienteCodigo: value }));
          }}
          className="w-full p-inputtext-sm text-xs md:text-sm"
          disabled={loading}
          placeholder="Ingrese Código de Cliente"
        />
      </FieldRow>
    </div>
  );

  const SectionClienteRut = () => (
    <div className="col-12 md:col-6 lg:col-6">
      <FieldRow id="clienteRut" label="RUT">
        <InputText
          id="clienteRut"
          value={filters.clienteRut}
          onChange={(e) => {
            const value = formatearRut(e.target.value);
            setFilters((prev) => ({ ...prev, clienteRut: value }));
          }}
          maxLength={12}
          className={`w-full p-inputtext-sm text-xs md:text-sm ${
            filters.clienteRut && !validarRut(filters.clienteRut)
              ? "p-invalid"
              : ""
          }`}
          disabled={loading}
          placeholder="Ingrese RUT de Cliente"
        />
      </FieldRow>
    </div>
  );

  const SectionTipoReporte = () => (
    <div className="col-12">
      <FieldRow id="tipoReporteCotizacion" label="Tipo Reporte">
        <div className="flex flex-wrap align-items-center gap-4">
          <div className="flex align-items-center">
            <RadioButton
              inputId="tipo_reporte_cotizacion"
              name="tipoReporteCotizacion"
              value="COTIZACION"
              onChange={(e) => onVariantChange && onVariantChange(e.value)}
              checked={selectedVariant === "COTIZACION"}
              disabled={loading}
            />
            <label htmlFor="tipo_reporte_cotizacion" className="ml-2">
              Cotizaciones
            </label>
          </div>

          <div className="flex align-items-center">
            <RadioButton
              inputId="tipo_reporte_nota_venta"
              name="tipoReporteCotizacion"
              value="NOTAVENTA"
              onChange={(e) => onVariantChange && onVariantChange(e.value)}
              checked={selectedVariant === "NOTAVENTA"}
              disabled={loading}
            />
            <label htmlFor="tipo_reporte_nota_venta" className="ml-2">
              Notas de venta
            </label>
          </div>

          <div className="flex align-items-center">
            <RadioButton
              inputId="tipo_reporte_bloqueada"
              name="tipoReporteCotizacion"
              value="BLOQUEADA"
              onChange={(e) => onVariantChange && onVariantChange(e.value)}
              checked={selectedVariant === "BLOQUEADA"}
              disabled={loading}
            />
            <label htmlFor="tipo_reporte_bloqueada" className="ml-2">
              Cotizaciones bloqueadas
            </label>
          </div>
        </div>
      </FieldRow>
    </div>
  );

  const SectionSackSelects = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="oficina" label="Oficina">
          <MultiSelect
            id="oficina"
            value={filters.oficina}
            options={data?.zonas || []}
            onChange={(e) => handleDropdownChange(e, "oficina")}
            optionLabel="descripcion"
            placeholder="Seleccione Oficina"
            showClear
            filter
            className="w-full p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
          />
        </FieldRow>
      </div>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="listaPrecios" label="Lista Precios">
          <MultiSelect
            id="listaPrecios"
            value={filters.listaPrecios}
            options={data?.listaPrecios || []}
            onChange={(e) => handleDropdownChange(e, "listaPrecios")}
            optionLabel={(item) =>
              item.descripcion + (item.almacen ? " " + item.almacen : "")
            }
            placeholder="Seleccione Lista"
            showClear
            filter
            className="w-full p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
          />
        </FieldRow>
      </div>
    </>
  );

  const SectionVendedor = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="vendedor" label="Vendedor">
          <MultiSelect
            id="vendedor"
            value={filters.vendedor}
            options={data?.vendedores || []}
            onChange={(e) => handleDropdownChange(e, "vendedor")}
            optionLabel="descripcion"
            placeholder="Seleccione Vendedor"
            showClear
            filter
            disabled={loading}
            className="w-full p-inputtext-sm text-xs md:text-sm"
          />
        </FieldRow>
      </div>
    </>
  );

  const SectionSackCanalOrigenEstado = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="canal" label="Canal">
          <MultiSelect
            id="canal"
            value={filters.canal}
            options={data?.canals || []}
            onChange={(e) => handleDropdownChange(e, "canal")}
            optionLabel="descripcion"
            placeholder="Seleccione Canal"
            showClear
            disabled={loading}
            className="w-full p-inputtext-sm text-xs md:text-sm"
          />
        </FieldRow>
      </div>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="origen" label="Origenes">
          <MultiSelect
            id="origen"
            value={filters.origen}
            options={data?.origens || []}
            onChange={(e) => handleDropdownChange(e, "origen")}
            optionLabel="descripcion"
            placeholder="Seleccione Origen"
            showClear
            disabled={loading}
            className="w-full p-inputtext-sm text-xs md:text-sm"
          />
        </FieldRow>
      </div>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="estado" label="Estados">
          <MultiSelect
            id="estado"
            value={filters.estado}
            options={data?.estados || []}
            onChange={(e) => handleDropdownChange(e, "estado")}
            optionLabel="descripcion"
            placeholder="Seleccione Estado"
            showClear
            filter
            disabled={loading}
            className="w-full p-inputtext-sm text-xs md:text-sm"
          />
        </FieldRow>
      </div>
    </>
  );

  const SectionVisualizacion = () => (
    <div className="col-12">
      <FieldRow id="visualizacion_todas" label="Visualización">
        <div className="flex flex-wrap align-items-center gap-4">
          <div className="flex align-items-center">
            <RadioButton
              inputId="visualizacion_todas"
              name="visualizacion"
              value="todas"
              onChange={(e) => handleDropdownChange(e, "visualizacion")}
              checked={filters.visualizacion === "todas"}
              disabled={loading}
            />
            <label htmlFor="visualizacion_todas" className="ml-2">
              Todas
            </label>
          </div>
          <div className="flex align-items-center">
            <RadioButton
              inputId="visualizacion_primeras"
              name="visualizacion"
              value="primeras"
              onChange={(e) => handleDropdownChange(e, "visualizacion")}
              checked={filters.visualizacion === "primeras"}
              disabled={loading}
            />
            <label htmlFor="visualizacion_primeras" className="ml-2">
              Primeras
            </label>
          </div>
          <div className="flex align-items-center">
            <RadioButton
              inputId="visualizacion_ultimas"
              name="visualizacion"
              value="ultimas"
              onChange={(e) => handleDropdownChange(e, "visualizacion")}
              checked={filters.visualizacion === "ultimas"}
              disabled={loading}
            />
            <label htmlFor="visualizacion_ultimas" className="ml-2">
              Últimas
            </label>
          </div>
          <div className="flex align-items-center">
            <Checkbox
              inputId="historia"
              name="historia"
              checked={filters.historia}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, historia: e.checked }))
              }
              disabled={loading}
            />
            <label htmlFor="historia" className="ml-2">
              Histórico
            </label>
          </div>
        </div>
      </FieldRow>
    </div>
  );

  const SectionDinarRazonSocial = () => (
    <div className="col-12 md:col-6 lg:col-6">
      <FieldRow id="razonSocial" label="Razón Social">
        <InputText
          id="razonSocial"
          value={filters.razonSocial ?? ""}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, razonSocial: e.target.value }))
          }
          className="w-full p-inputtext-sm text-xs md:text-sm"
          placeholder="Ingrese Razón Social"
          disabled={loading}
        />
      </FieldRow>
    </div>
  );

  const SectionDinarProducto = () => (
    <div className="col-12 md:col-6 lg:col-6">
      <FieldRow id="codigoproducto" label="Producto">
        <div className="flex gap-2">
          <InputText
            id="codigoproducto"
            value={filters.codigoproducto ?? ""}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                codigoproducto: e.target.value,
              }))
            }
            className="w-full p-inputtext-sm text-xs md:text-sm"
            placeholder="Ingrese Código"
            disabled={loading}
          />
          <InputText
            id="descripcionproducto"
            value={filters.descripcionproducto ?? ""}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                descripcionproducto: e.target.value,
              }))
            }
            className="w-full p-inputtext-sm text-xs md:text-sm"
            placeholder="Ingrese Descripción"
            disabled={loading}
          />
        </div>
      </FieldRow>
    </div>
  );

  const SectionNotaVentaFolios = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="notaVentaDesde" label="Nota Venta Desde">
          <InputText
            id="notaVentaDesde"
            value={filters.notaVentaDesde ?? ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              setFilters((prev) => ({ ...prev, notaVentaDesde: value }));
            }}
            className="w-full p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
            placeholder="Ingrese N° de Nota Venta Desde"
          />
        </FieldRow>
      </div>

      <div className="col-12 md:col-6 lg:col-6">
        <FieldRow id="notaVentaHasta" label="Nota Venta Hasta">
          <InputText
            id="notaVentaHasta"
            value={filters.notaVentaHasta ?? ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              setFilters((prev) => ({ ...prev, notaVentaHasta: value }));
            }}
            className="w-full p-inputtext-sm text-xs md:text-sm"
            disabled={loading}
            placeholder="Ingrese N° de Nota Venta Hasta"
          />
        </FieldRow>
      </div>
    </>
  );

  const SectionNotaVentaRadios = () => (
    <>
      <div className="col-12 md:col-6 lg:col-6">
        <div className="mt-2">
          <FieldRow id="estado_todos" label="Estado">
            <div className="flex flex-wrap align-items-center gap-4">
              <div className="flex align-items-center">
                <RadioButton
                  inputId="estado_todos"
                  name="estado"
                  value="todos"
                  onChange={(e) => handleDropdownChange(e, "estado")}
                  checked={filters.estado === "todos"}
                  disabled={loading}
                />
                <label htmlFor="estado_todos" className="ml-2">
                  Todos
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="estado_confirmados"
                  name="estado"
                  value="confirmados"
                  onChange={(e) => handleDropdownChange(e, "estado")}
                  checked={filters.estado === "confirmados"}
                  disabled={loading}
                />
                <label htmlFor="estado_confirmados" className="ml-2">
                  Confirmados
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="estado_pendientes"
                  name="estado"
                  value="pendientes"
                  onChange={(e) => handleDropdownChange(e, "estado")}
                  checked={filters.estado === "pendientes"}
                  disabled={loading}
                />
                <label htmlFor="estado_pendientes" className="ml-2">
                  Pendientes
                </label>
              </div>
            </div>
          </FieldRow>
        </div>

        <div className="mt-5">
          <FieldRow id="entrega_todos" label="Tipo de Entrega">
            <div className="flex flex-wrap align-items-center gap-4">
              <div className="flex align-items-center">
                <RadioButton
                  inputId="entrega_todos"
                  name="tipodetipoentrega"
                  value="todos"
                  onChange={(e) => handleDropdownChange(e, "tipodetipoentrega")}
                  checked={filters.tipodetipoentrega === "todos"}
                  disabled={loading}
                />
                <label htmlFor="entrega_todos" className="ml-2">
                  Todos
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="entrega_retiro"
                  name="tipodetipoentrega"
                  value="retiro"
                  onChange={(e) => handleDropdownChange(e, "tipodetipoentrega")}
                  checked={filters.tipodetipoentrega === "retiro"}
                  disabled={loading}
                />
                <label htmlFor="entrega_retiro" className="ml-2">
                  Retiro
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="entrega_despacho"
                  name="tipodetipoentrega"
                  value="despacho"
                  onChange={(e) => handleDropdownChange(e, "tipodetipoentrega")}
                  checked={filters.tipodetipoentrega === "despacho"}
                  disabled={loading}
                />
                <label htmlFor="entrega_despacho" className="ml-2">
                  Despacho
                </label>
              </div>
            </div>
          </FieldRow>
        </div>
      </div>
    </>
  );

  const SectionSpacer = () => (
    <div className="col-12 md:col-6 lg:col-6">
      <span></span>
    </div>
  );

  const sectionRenderers = {
    tipoReporte: SectionTipoReporte,
    dates: SectionDates,
    folios: SectionFolios,
    clienteCodigo: SectionClienteCodigo,
    clienteRut: SectionClienteRut,
    sackSelects: isSack ? SectionSackSelects : null,
    sackCanalOrigenEstado: isSack ? SectionSackCanalOrigenEstado : null,
    visualizacion: isSack ? SectionVisualizacion : null,
    dinarRazonSocial: isDinar ? SectionDinarRazonSocial : null,
    dinarProducto: isDinar ? SectionDinarProducto : null,
    notaVentaFolios: isDinar ? SectionNotaVentaFolios : null,
    notaVentaRadios: isDinar ? SectionNotaVentaRadios : null,
    spacer: SectionSpacer,
    vendedor: SectionVendedor,
  };

  return (
    <div className="grid p-fluid">
      <div className="col-12 lg:col-8">
        <div className="grid p-fluid">
          {config.sections.map((sectionKey) => {
            const renderSection = sectionRenderers[sectionKey];
            if (!renderSection) return null;
            return (
              <Fragment key={sectionKey}>
                {renderSection()}
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className="col-12 lg:col-2 mx-auto flex flex-column justify-content-center align-items-center align-self-stretch gap-2">
        <Button
          label="Ejecutar"
          icon={loading ? "pi pi-spin pi-spinner" : "pi pi-search"}
          className="p-button-primary p-button-sm"
          onClick={() => onSearch && onSearch(filters)}
        />

        <Button
          label="Exportar"
          icon={exporting ? "pi pi-spin pi-spinner" : "pi pi-file-excel"}
          className="p-button-success p-button-sm"
          disabled={exporting}
          onClick={() => {
            if (!filters.fechaDesde || !filters.fechaHasta) {
              alert("Debe ingresar Fecha Desde y Fecha Hasta para exportar");
              return;
            }
            onExport && onExport(filters);
          }}
        />

        <Button
          label="Limpiar"
          icon="pi pi-eraser"
          className="p-button-secondary p-button-sm"
          onClick={handleClearLocal}
        />
      </div>
    </div>
  );
};

export default Filtros;
